import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Logo } from '@resolution-processing/balance';
import { useAuth } from '../auth/AuthContext.tsx';
import styles from './LoginScreen.module.css';

interface LoginScreenProps {
  title?: string;
}

type Step = 'email' | 'code';

export function LoginScreen({ title = 'QC Engine' }: LoginScreenProps) {
  const { t } = useTranslation('ui');
  const { login, requestMagicLink, verifyCode } = useAuth();

  // Which step of the magic-link flow we're on
  const [step, setStep] = useState<Step>('email');

  // Form field values
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');

  // Loading / error state for each action
  const [emailLoading, setEmailLoading] = useState(false);
  const [codeLoading, setCodeLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  // Visible success notice after a successful resend; cleared on any next action
  const [resendSuccess, setResendSuccess] = useState(false);

  const handleEmailSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setEmailLoading(true);
    setError(undefined);
    setResendSuccess(false);
    const err = await requestMagicLink(email.trim());
    setEmailLoading(false);
    if (err) {
      // Use the localised cooldown string when the server returns a 429 (IP
      // rate limit on initial submit), matching how handleResend handles it.
      if (err.status === 429) {
        setError(t('auth.magicLink.resendCooldown'));
      } else {
        setError(err.message);
      }
    } else {
      setStep('code');
      setCode('');
    }
  };

  const handleCodeSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return;
    setCodeLoading(true);
    setError(undefined);
    setResendSuccess(false);
    const err = await verifyCode(code.trim());
    setCodeLoading(false);
    if (err) {
      setError(err);
    }
    // On success AuthContext sets the user — LoginScreen unmounts automatically.
  };

  const handleResend = async () => {
    setResendLoading(true);
    setError(undefined);
    setResendSuccess(false);
    const err = await requestMagicLink(email.trim());
    setResendLoading(false);
    if (err) {
      // Use the localised cooldown string when the server returns a 429.
      if (err.status === 429) {
        setError(t('auth.magicLink.resendCooldown'));
      } else {
        setError(err.message);
      }
    } else {
      // Show a brief confirmation so the user knows the resend worked.
      setResendSuccess(true);
    }
  };

  const handleBackToEmail = () => {
    setStep('email');
    setError(undefined);
    setResendSuccess(false);
    setCode('');
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <Logo color="blue" height={80} />
        <h1 className={styles.title}>{title}</h1>
        <p className={styles.subtitle}>Sign in to continue</p>

        {/* Foundation (staff) login — always visible */}
        <Button
          variant="primary"
          onClick={login}
          className={styles.loginButton}
        >
          Login with Foundation
        </Button>

        {/* Divider between Foundation and magic-link */}
        <div className={styles.divider}>{t('auth.magicLink.divider')}</div>

        {step === 'email' && (
          <form className={styles.magicLinkForm} onSubmit={handleEmailSubmit} noValidate>
            <p className={styles.magicLinkHint}>{t('auth.magicLink.emailHint')}</p>
            <Input
              type="email"
              name="email"
              label={t('auth.magicLink.emailLabel')}
              placeholder={t('auth.magicLink.emailPlaceholder')}
              value={email}
              onChange={setEmail}
              disabled={emailLoading}
              required
              autoComplete="on"
              error={!!error}
              helperText={error}
            />
            <Button
              variant="primary"
              type="submit"
              className={styles.submitButton}
              disabled={emailLoading || !email.trim()}
            >
              {emailLoading ? t('auth.magicLink.emailLoading') : t('auth.magicLink.emailSubmit')}
            </Button>
          </form>
        )}

        {step === 'code' && (
          <div className={styles.magicLinkForm}>
            <p className={styles.codeSentNotice}>
              {t('auth.magicLink.codeSent', { email })}
            </p>
            <form onSubmit={handleCodeSubmit} noValidate>
              <div className={styles.magicLinkForm}>
                <Input
                  type="text"
                  name="code"
                  label={t('auth.magicLink.codeLabel')}
                  placeholder={t('auth.magicLink.codePlaceholder')}
                  value={code}
                  onChange={setCode}
                  disabled={codeLoading}
                  required
                  autoComplete="on"
                  pattern="^\d{6}$"
                  error={!!error}
                  helperText={error}
                />
                <Button
                  variant="primary"
                  type="submit"
                  className={styles.submitButton}
                  disabled={codeLoading || code.trim().length !== 6}
                >
                  {codeLoading ? t('auth.magicLink.codeLoading') : t('auth.magicLink.codeSubmit')}
                </Button>
              </div>
            </form>

            {/* Visible confirmation on a successful resend */}
            {resendSuccess && (
              <p className={styles.resendSuccess}>{t('auth.magicLink.resendSuccess')}</p>
            )}

            <button
              type="button"
              className={styles.textButton}
              onClick={handleResend}
              disabled={resendLoading}
            >
              {resendLoading ? t('auth.magicLink.resendLoading') : t('auth.magicLink.resend')}
            </button>

            <button
              type="button"
              className={styles.textButton}
              onClick={handleBackToEmail}
              disabled={codeLoading || resendLoading}
            >
              {t('auth.magicLink.backToEmail')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
